import React, { useEffect, useState } from 'react';
import { User, Bell, Shield, Palette, Save } from 'lucide-react';
import './AdminSettings.css';
import { API_BASE_URL } from '../../api';
import { getApiKey } from '../../utils/auth';

const LOCAL_SETTINGS_STORAGE_KEY = 'admin_portal_settings_preferences';

const readStoredPreferences = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(LOCAL_SETTINGS_STORAGE_KEY);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch (error) {
    console.error('Failed to read admin preferences from localStorage:', error);
    return null;
  }
};

const getInitialSettings = () => {
  const storedPreferences = readStoredPreferences();

  return {
    profile: {
      username: '',
      email: '',
      role: ''
    },
    notifications: {
      emailNotifications: storedPreferences?.notifications?.emailNotifications ?? true,
      pushNotifications: storedPreferences?.notifications?.pushNotifications ?? true,
      vendorUpdates: storedPreferences?.notifications?.vendorUpdates ?? true,
      systemAlerts: storedPreferences?.notifications?.systemAlerts ?? true,
      weeklyReports: storedPreferences?.notifications?.weeklyReports ?? false
    },
    security: {
      twoFactorAuth: storedPreferences?.security?.twoFactorAuth ?? false,
      sessionTimeout: storedPreferences?.security?.sessionTimeout ?? '30',
      passwordExpiry: storedPreferences?.security?.passwordExpiry ?? '90',
      loginAlerts: storedPreferences?.security?.loginAlerts ?? true
    },
    appearance: {
      theme: storedPreferences?.appearance?.theme ?? 'light',
      sidebarCollapsed: storedPreferences?.appearance?.sidebarCollapsed ?? false,
      compactMode: storedPreferences?.appearance?.compactMode ?? false
    }
  };
};

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('profile');
  const [settings, setSettings] = useState(getInitialSettings);
  const [saveState, setSaveState] = useState('idle');
  const [saveMessage, setSaveMessage] = useState('');

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'appearance', label: 'Appearance', icon: Palette }
  ];

  useEffect(() => {
    if (!saveMessage) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setSaveMessage('');
      setSaveState('idle');
    }, 4000);

    return () => window.clearTimeout(timeoutId);
  }, [saveMessage]);

  const handleSettingChange = (category, key, value) => {
    setSettings((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
  };

  const persistLocalPreferences = () => {
    if (typeof window === 'undefined') {
      return;
    }

    const localPreferences = {
      notifications: settings.notifications,
      security: settings.security,
      appearance: settings.appearance
    };

    try {
      window.localStorage.setItem(
        LOCAL_SETTINGS_STORAGE_KEY,
        JSON.stringify(localPreferences)
      );
    } catch (error) {
      console.error('Failed to save admin preferences to localStorage:', error);
    }
  };

  const loadAdminProfile = async () => {
    try {
      const apiKey = getApiKey();
      const res = await fetch(`${API_BASE_URL}/api/admin/profile`, {
        headers: { 'x-admin-key': apiKey }
      });

      if (res.ok) {
        const data = await res.json();
        setSettings((prev) => ({
          ...prev,
          profile: {
            username: data.username || '',
            email: data.email || '',
            role: data.role || ''
          }
        }));
      }
    } catch (error) {
      console.error('Load admin profile error:', error);
    }
  };

  useEffect(() => {
    loadAdminProfile();
  }, []);

  const handleSave = async () => {
    persistLocalPreferences();

    if (activeTab !== 'profile') {
      setSaveState('success');
      setSaveMessage('Settings saved locally for this browser.');
      return;
    }

    try {
      const apiKey = getApiKey();
      const res = await fetch(`${API_BASE_URL}/api/admin/profile`, {
        method: 'PUT',
        headers: {
          'x-admin-key': apiKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: settings.profile.username,
          email: settings.profile.email
        })
      });

      if (!res.ok) {
        const errorPayload = await res.json().catch(() => ({}));
        throw new Error(errorPayload.error || 'Failed to save profile');
      }

      await loadAdminProfile();
      setSaveState('success');
      setSaveMessage('Profile updated and local settings saved.');
    } catch (error) {
      console.error('Save profile error:', error);
      setSaveState('error');
      setSaveMessage(`${error.message}. Local settings were still saved.`);
    }
  };

  const renderProfileSettings = () => (
    <div className="settings-section">
      <h3>Admin Profile</h3>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Username</label>
          <input
            type="text"
            className="form-input"
            value={settings.profile.username}
            onChange={(e) => handleSettingChange('profile', 'username', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Email</label>
          <input
            type="email"
            className="form-input"
            value={settings.profile.email}
            onChange={(e) => handleSettingChange('profile', 'email', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Role</label>
          <input
            type="text"
            className="form-input"
            value={settings.profile.role}
            disabled
          />
          <small className="field-hint">Role cannot be changed</small>
        </div>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="settings-section">
      <h3>Notification Preferences</h3>
      <div className="settings-list">
        <div className="setting-item">
          <div className="setting-info">
            <h4>Email Notifications</h4>
            <p>Receive important updates via email</p>
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.notifications.emailNotifications}
              onChange={(e) =>
                handleSettingChange('notifications', 'emailNotifications', e.target.checked)
              }
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <h4>Push Notifications</h4>
            <p>Receive real-time notifications</p>
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.notifications.pushNotifications}
              onChange={(e) =>
                handleSettingChange('notifications', 'pushNotifications', e.target.checked)
              }
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <h4>Vendor Updates</h4>
            <p>Get notified about vendor activities</p>
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.notifications.vendorUpdates}
              onChange={(e) =>
                handleSettingChange('notifications', 'vendorUpdates', e.target.checked)
              }
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <h4>System Alerts</h4>
            <p>Receive critical system notifications</p>
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.notifications.systemAlerts}
              onChange={(e) =>
                handleSettingChange('notifications', 'systemAlerts', e.target.checked)
              }
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <h4>Weekly Reports</h4>
            <p>Receive weekly platform reports</p>
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.notifications.weeklyReports}
              onChange={(e) =>
                handleSettingChange('notifications', 'weeklyReports', e.target.checked)
              }
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="settings-section">
      <h3>Security Settings</h3>
      <div className="settings-list">
        <div className="setting-item">
          <div className="setting-info">
            <h4>Two-Factor Authentication</h4>
            <p>Add an extra layer of security to your account</p>
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.security.twoFactorAuth}
              onChange={(e) =>
                handleSettingChange('security', 'twoFactorAuth', e.target.checked)
              }
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <h4>Session Timeout</h4>
            <p>Automatically log out after inactivity</p>
          </div>
          <select
            className="form-select"
            value={settings.security.sessionTimeout}
            onChange={(e) => handleSettingChange('security', 'sessionTimeout', e.target.value)}
          >
            <option value="15">15 minutes</option>
            <option value="30">30 minutes</option>
            <option value="60">1 hour</option>
            <option value="120">2 hours</option>
          </select>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <h4>Password Expiry</h4>
            <p>Require password change after specified days</p>
          </div>
          <select
            className="form-select"
            value={settings.security.passwordExpiry}
            onChange={(e) => handleSettingChange('security', 'passwordExpiry', e.target.value)}
          >
            <option value="30">30 days</option>
            <option value="60">60 days</option>
            <option value="90">90 days</option>
            <option value="180">180 days</option>
            <option value="365">1 year</option>
          </select>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <h4>Login Alerts</h4>
            <p>Get notified of new login attempts</p>
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.security.loginAlerts}
              onChange={(e) =>
                handleSettingChange('security', 'loginAlerts', e.target.checked)
              }
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderAppearanceSettings = () => (
    <div className="settings-section">
      <h3>Appearance Settings</h3>
      <div className="settings-list">
        <div className="setting-item">
          <div className="setting-info">
            <h4>Theme</h4>
            <p>Choose your preferred color scheme</p>
          </div>
          <select
            className="form-select"
            value={settings.appearance.theme}
            onChange={(e) => handleSettingChange('appearance', 'theme', e.target.value)}
          >
            <option value="light">Light</option>
            <option value="dark">Dark</option>
            <option value="auto">Auto</option>
          </select>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <h4>Sidebar Collapsed</h4>
            <p>Start with sidebar collapsed by default</p>
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.appearance.sidebarCollapsed}
              onChange={(e) =>
                handleSettingChange('appearance', 'sidebarCollapsed', e.target.checked)
              }
            />
            <span className="toggle-slider"></span>
          </label>
        </div>

        <div className="setting-item">
          <div className="setting-info">
            <h4>Compact Mode</h4>
            <p>Use compact spacing for better screen utilization</p>
          </div>
          <label className="toggle">
            <input
              type="checkbox"
              checked={settings.appearance.compactMode}
              onChange={(e) =>
                handleSettingChange('appearance', 'compactMode', e.target.checked)
              }
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return renderProfileSettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'security':
        return renderSecuritySettings();
      case 'appearance':
        return renderAppearanceSettings();
      default:
        return renderProfileSettings();
    }
  };

  return (
    <div className="settings">
      <div className="settings-header">
        <div className="header-content">
          <h1>Admin Settings</h1>
          <p>Manage your admin profile, preferences, and appearance</p>
        </div>

        <div className="settings-header-actions">
          {saveMessage ? (
            <div className={`save-status ${saveState === 'error' ? 'error' : 'success'}`}>
              {saveMessage}
            </div>
          ) : null}

          <button className="btn btn-primary" onClick={handleSave}>
            <Save size={20} />
            Save Changes
          </button>
        </div>
      </div>

      <div className="settings-container">
        <div className="settings-sidebar">
          <nav className="settings-nav">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <Icon size={20} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="settings-content">{renderContent()}</div>
      </div>
    </div>
  );
};

export default AdminSettings;
